import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React from "react";

import { getOrderEvents, type EventView } from "@/api/order_journal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/format";
import { describeEvent } from "@/lib/order-journal";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Лента действий по заказу (по времени): кто и что сделал, было → стало.
 * События пишутся с 04.10.2026 — у сборок раньше лента пустая, и это
 * сказано словами.
 */
export function EventsTab({
  orderRef,
  showSession,
}: {
  orderRef: string;
  showSession: boolean;
}): React.ReactElement {
  const q = useInfiniteQuery({
    queryKey: ["order-journal-events", orderRef],
    queryFn: ({ pageParam }) => getOrderEvents(orderRef, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, all) =>
      last.has_more ? all.reduce((n, p) => n + p.items.length, 0) : undefined,
  });

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-10 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
        События не загрузились: {apiErrorText(q.error)}{" "}
        <button type="button" className="underline" onClick={() => void q.refetch()}>
          Повторить
        </button>
      </div>
    );
  }
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  if (items.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Событий нет: история действий ведётся с 04.10.2026.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <Card className="divide-y">
        {items.map((ev) => (
          <EventRow key={ev.id} ev={ev} showSession={showSession} />
        ))}
      </Card>
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage && <Loader2 className="size-4 animate-spin" />}
            Показать ещё
          </Button>
        </div>
      )}
    </div>
  );
}

function EventRow({ ev, showSession }: { ev: EventView; showSession: boolean }): React.ReactElement {
  const t = describeEvent(ev);
  return (
    <div data-testid="journal-event" className="space-y-0.5 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="tabular-nums text-xs text-muted-foreground">{fmtDateTime(ev.created_at)}</span>
        <span className="font-medium">{t.title}</span>
        <span className="text-xs text-muted-foreground">{ev.user ?? "автор не записан"}</span>
        {showSession && ev.session_display_number !== null && (
          <span className="text-xs text-muted-foreground">проверка № {ev.session_display_number}</span>
        )}
        {t.manual && (
          <span className="rounded bg-amber-100 px-1 text-xs font-medium text-amber-800">{t.manual}</span>
        )}
      </div>
      {t.detail && <div className="break-words text-xs text-muted-foreground">{t.detail}</div>}
      {(t.before || t.after) && (
        <div className="break-words text-xs">
          было: <span className="font-medium">{t.before || "—"}</span> → стало:{" "}
          <span className="font-medium">{t.after || "—"}</span>
        </div>
      )}
    </div>
  );
}
